import type { MergeField, TemplateTypeConfig } from '../types/template';

const draftFields: MergeField[] = [
  {
    tag: 'INSERT COVER LETTER',
    description: 'STRICT TAG: The generated cover letter will be inserted here.',
    type: 'subdoc',
  },
  {
    tag: 'INSERT EXECUTIVE SUMMARY',
    description: 'STRICT TAG: The AI-generated Executive Summary will be inserted here.',
    type: 'subdoc',
  },
  {
    tag: 'INSERT DRAFT SECTIONS',
    description:
      'STRICT TAG: The main placeholder for the AI-generated response. The system will automatically generate and format the entire proposal response here.',
    type: 'subdoc',
  },
  {
    tag: 'INSERT Company history, 400 words',
    description: 'The AI will generate a company history constrained to approximately 400 words.',
    type: 'string',
  },
  {
    tag: 'INSERT Project approach, focusing on risk mitigation',
    description: 'The AI will generate a project approach with a specific thematic focus.',
    type: 'string',
  },
  {
    tag: `INSERT Proposed Risk Management Plan\nEXAMPLE:\nRisk: Weather delays\nMitigation: \n• Maintain flexible scheduling\n• Secure alternative indoor tasks\n• Monitor weather forecasts closely`,
    description:
      'Provides both the prompt ("INSERT...") and the exact layout ("EXAMPLE..."). Apply standard Word formatting (like bolding "Risk:" or using Word\'s bullet points) directly to this text, and the AI will preserve it in the generated response.',
    type: 'string',
  },
  {
    tag: `INSERT Key Project Milestones\nEXAMPLE:\nMilestone 1: [Name] - [Timeline]\n• Key deliverable 1\n• Key deliverable 2`,
    description:
      'Another example requesting a structured layout. The AI will populate the bracketed fields and add the appropriate number of bullet points based on the tender.',
    type: 'string',
  },
];

const cvFields: MergeField[] = [
  {
    tag: 'INSERT Full name',
    description: "Simple extraction. The AI will output the candidate's full name.",
    type: 'string',
  },
  {
    tag: 'INSERT Profession',
    description:
      "Simple extraction. The AI will output the candidate's primary profession or title.",
    type: 'string',
  },
  {
    tag: 'INSERT Bio, 250 words (half page)',
    description:
      'Instructs the AI to write a concise biographical summary constrained to a half page.',
    type: 'string',
  },
  {
    tag: 'INSERT Bio, 450 words (full page)',
    description:
      'Instructs the AI to write a comprehensive biographical summary constrained to a full page.',
    type: 'string',
  },
  {
    tag: 'INSERT Professional summary, focusing on leadership, 200 words',
    description:
      "Instructs the AI to tailor the candidate's summary specifically to the tender's leadership requirements.",
    type: 'string',
  },
  {
    tag: `INSERT Previous roles and responsibilities\nEXAMPLE:\nHuman Factors Specialist for Research Project X\n2018 – 2020\n• Responsibility 1\n• Responsibility 2`,
    description:
      'Forces the AI to format the output exactly like the provided multi-line example. Apply your desired font, colors, and styling directly in Word.',
    type: 'string',
  },
  {
    tag: `INSERT Key Qualifications and Certifications\nEXAMPLE:\n• B.S. Engineering, University of Technology, 2012\n• Certified Project Management Professional (PMP)`,
    description: "Extracts and formats the candidate's qualifications as a bulleted list.",
    type: 'string',
  },
  {
    tag: `INSERT Relevant Project Experience, 3 projects\nEXAMPLE:\nProject: [Project Name]\nClient: [Client Name]\nValue: $[Value]\nRole: [Candidate Role]\nDescription: [Brief 2-sentence description of the work and outcome]`,
    description:
      "Instructs the AI to select the 3 most relevant projects from the candidate's background and format them into a structured data block.",
    type: 'string',
  },
  {
    tag: `INSERT Key Skills matrix\nEXAMPLE:\nTechnical Skills: [Skill 1], [Skill 2], [Skill 3]\nManagement Skills: [Skill 1], [Skill 2]`,
    description: "Forces the AI to categorise the candidate's skills into a specific format.",
    type: 'string',
  },
];

export const TEMPLATE_CONFIGS: Record<string, TemplateTypeConfig> = {
  draft: {
    label: '',
    description: '',
    requiredTags: ['INSERT DRAFT SECTIONS'],
    suggestedTags: [
      'INSERT EXECUTIVE SUMMARY',
      'INSERT Company history, 400 words',
      'INSERT Project approach, focusing on risk mitigation',
      `INSERT Proposed Risk Management Plan\nEXAMPLE:\nRisk: Weather delays\nMitigation: \n• Maintain flexible scheduling\n• Secure alternative indoor tasks\n• Monitor weather forecasts closely`,
      `INSERT Key Project Milestones\nEXAMPLE:\nMilestone 1: [Name] - [Timeline]\n• Key deliverable 1\n• Key deliverable 2`,
    ],
    optionalTags: draftFields
      .map((f) => f.tag)
      .filter(
        (t) =>
          t !== 'INSERT DRAFT SECTIONS' &&
          ![
            'INSERT EXECUTIVE SUMMARY',
            'INSERT Company history, 400 words',
            'INSERT Project approach, focusing on risk mitigation',
            `INSERT Proposed Risk Management Plan\nEXAMPLE:\nRisk: Weather delays\nMitigation: \n• Maintain flexible scheduling\n• Secure alternative indoor tasks\n• Monitor weather forecasts closely`,
            `INSERT Key Project Milestones\nEXAMPLE:\nMilestone 1: [Name] - [Timeline]\n• Key deliverable 1\n• Key deliverable 2`,
          ].includes(t)
      ),
    allowedTags: draftFields,
  },
  cv: {
    label: '',
    description: '',
    requiredTags: [], // Form style doesn't require specific merge tags
    suggestedTags: [
      'INSERT Full name',
      'INSERT Profession',
      'INSERT Bio, 250 words (half page)',
      'INSERT Bio, 450 words (full page)',
      'INSERT Professional summary, focusing on leadership, 200 words',
      `INSERT Previous roles and responsibilities\nEXAMPLE:\nHuman Factors Specialist for Research Project X\n2018 – 2020\n• Responsibility 1\n• Responsibility 2`,
      `INSERT Key Qualifications and Certifications\nEXAMPLE:\n• B.S. Engineering, University of Technology, 2012\n• Certified Project Management Professional (PMP)`,
      `INSERT Relevant Project Experience, 3 projects\nEXAMPLE:\nProject: [Project Name]\nClient: [Client Name]\nValue: $[Value]\nRole: [Candidate Role]\nDescription: [Brief 2-sentence description of the work and outcome]`,
      `INSERT Key Skills matrix\nEXAMPLE:\nTechnical Skills: [Skill 1], [Skill 2], [Skill 3]\nManagement Skills: [Skill 1], [Skill 2]`,
    ],
    optionalTags: [],
    allowedTags: cvFields,
  },
};
